import React from 'react';

/**
 * BundleCategoryPlaceholder
 *
 * Elegant, minimal, neutral category graphic shown on a Quick Order bundle
 * card when the bundle has no uploaded image. It deliberately renders a
 * GENERIC media silhouette (never real artwork, album covers, artist names
 * or product-specific detail) so it reads as an illustration of the media
 * category — not a preview of the customer's final product.
 *
 * Reusable local asset: inline SVG, responsive (scales to its container),
 * OWNTUNE-branded (neutral slate base + a subtle amber accent).
 *
 * Families: vinyl | cd | dvd | bluray (falls back to a generic disc).
 */

const FAMILY_LABEL = {
    vinyl: 'Vinyl Record',
    cd: 'Compact Disc',
    dvd: 'DVD',
    bluray: 'Blu-ray Disc',
};

// Blu-ray gets a faint cool tint on the sheen; the rest stay neutral slate.
const SHEEN = {
    vinyl: { a: '#334155', b: '#0f172a' },
    cd: { a: '#cbd5e1', b: '#94a3b8' },
    dvd: { a: '#cbd5e1', b: '#94a3b8' },
    bluray: { a: '#bfdbfe', b: '#93c5fd' },
};

function VinylSilhouette({ uid }) {
    return (
        <g>
            <circle cx="60" cy="60" r="52" fill={`url(#disc-${uid})`} />
            {[46, 40, 34, 28, 22].map((r) => (
                <circle key={r} cx="60" cy="60" r={r} fill="none" stroke="#0b1220" strokeOpacity="0.35" strokeWidth="1" />
            ))}
            {/* center label — generic amber accent, no artwork */}
            <circle cx="60" cy="60" r="16" fill="url(#label-amber)" />
            <circle cx="60" cy="60" r="3.2" fill="#fffbeb" />
        </g>
    );
}

function OpticalSilhouette({ uid }) {
    return (
        <g>
            <circle cx="60" cy="60" r="52" fill={`url(#disc-${uid})`} />
            <circle cx="60" cy="60" r="52" fill={`url(#sheen-${uid})`} />
            {/* clear inner hub + center hole */}
            <circle cx="60" cy="60" r="18" fill="#f1f5f9" />
            <circle cx="60" cy="60" r="17" fill="none" stroke="#94a3b8" strokeOpacity="0.5" strokeWidth="1" />
            <circle cx="60" cy="60" r="8" fill="#e2e8f0" />
            <circle cx="60" cy="60" r="8" fill="none" stroke="#94a3b8" strokeOpacity="0.6" strokeWidth="1" />
        </g>
    );
}

export const BundleCategoryPlaceholder = ({ family = 'vinyl', showLabel = true, className = '' }) => {
    const fam = ['vinyl', 'cd', 'dvd', 'bluray'].includes(family) ? family : 'vinyl';
    const uid = fam;
    const sheen = SHEEN[fam];
    const isVinyl = fam === 'vinyl';

    return (
        <div
            className={`flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 ${className}`}
            data-testid={`bundle-category-placeholder-${fam}`}
            aria-label={`${FAMILY_LABEL[fam]} category illustration`}
            role="img"
        >
            <svg
                viewBox="0 0 120 120"
                className="h-[46%] max-h-24 w-auto drop-shadow-sm"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <radialGradient id={`disc-${uid}`} cx="40%" cy="35%" r="75%">
                        <stop offset="0%" stopColor={isVinyl ? '#1e293b' : sheen.a} />
                        <stop offset="100%" stopColor={isVinyl ? '#0b1220' : sheen.b} />
                    </radialGradient>
                    {!isVinyl && (
                        <radialGradient id={`sheen-${uid}`} cx="62%" cy="68%" r="60%">
                            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
                            <stop offset="55%" stopColor="#ffffff" stopOpacity="0.05" />
                            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                        </radialGradient>
                    )}
                    <linearGradient id="label-amber" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="hsl(42 96% 55%)" />
                        <stop offset="100%" stopColor="hsl(35 92% 45%)" />
                    </linearGradient>
                </defs>
                {isVinyl ? <VinylSilhouette uid={uid} /> : <OpticalSilhouette uid={uid} />}
            </svg>
            {showLabel && (
                <span className="mt-2 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
                    {FAMILY_LABEL[fam]}
                </span>
            )}
        </div>
    );
};

export default BundleCategoryPlaceholder;
